<template>
  <div class="pa-3 px-4">
    <div class="calendar-settings-bar pb-3">
      <v-btn-toggle v-model="viewMode" mandatory>
        <v-btn flat value="gregorian" small>Gregorian</v-btn>
        <v-btn flat value="jewish" small>Jewish</v-btn>
      </v-btn-toggle>
      <v-divider vertical class="calendar-settings-divider"></v-divider>
      <v-btn-toggle @change="languageChanged" v-model="language" mandatory>
        <v-btn flat value="en" small>English</v-btn>
        <v-btn flat value="he" small>אידיש</v-btn>
      </v-btn-toggle>
    </div>

    <div
      class="mt-0 pt-0"
      style="display: flex; align-items: center; position: relative;"
    >
      <v-layout row justify-space-between class="mt-0 pt-0">
        <div class="calendar-left-actions">
          <v-btn
            class="cal-action-btn"
            color="success"
            outline
            title="Export to Excel"
            @click="exportDialog = true"
          >
            <v-icon left small>mdi-file-export</v-icon>
            Export
          </v-btn>
          <v-btn
            :loading="loadingPrint"
            class="cal-action-btn"
            color="success"
            outline
            title="Print the current month"
            @click="handleCalendarPrint"
          >
            <v-icon left small>mdi-printer </v-icon>
            Print
          </v-btn>
        </div>

        <v-layout row style="display: flex; justify-content: center;">
          <v-btn
            class="cal-action-btn"
            color="success"
            outline
            @click="prevMonth()"
            title="Previous Month"
          >
            <v-icon left>mdi-chevron-left</v-icon>
            Prev
          </v-btn>
          <div class="px-3 mt-3" style="text-align: center;">
            <div v-if="viewMode === 'jewish'">
              <div class="headline">
                {{ currentHebrewMonthLabel }}
              </div>
              <div>
                {{ currentGregorianMonthLabel }}
              </div>
            </div>
            <div v-else>
              <div class="headline">
                {{ currentGregorianMonthLabel }}
              </div>
              <div>
                {{ currentHebrewMonthLabel }}
              </div>
            </div>
          </div>
          <v-btn
            class="cal-action-btn"
            color="success"
            outline
            @click="nextMonth()"
            title="Next Month"
          >
            Next
            <v-icon right>mdi-chevron-right</v-icon>
          </v-btn>
        </v-layout>
        <v-btn
          class="cal-action-btn"
          color="success"
          outline
          @click="goToMounthDialog = true"
          title="Quick Jump to Month"
        >
          <v-icon left small>mdi-calendar-search</v-icon>
          Go to Month
        </v-btn>
      </v-layout>
    </div>
    <v-flex class="pt-3" style="display: flex; justify-content: center;">
      <v-sheet width="100%">
        <div v-if="loading" class="calendar-loading-overlay">
          <v-progress-circular
            indeterminate
            color="success"
            size="60"
          ></v-progress-circular>
        </div>
        <v-calendar
          class="calendar-container"
          @change="loadDayData"
          ref="calendar"
          type="custom-weekly"
          :start="calendarStart"
          :end="calendarEnd"
        >
          <template v-slot:day="{ date }">
            <template v-if="dayDataMap[date]">
              <div
                class="day-bg"
                :class="{
                  'day-bg-outside-month':
                    dayDataMap[date].isOutsidePrimaryMonth,
                }"
                :style="{ backgroundColor: dayDataMap[date].bgColor }"
              ></div>
              <div
                :class="{
                  'day-content-outside-month':
                    dayDataMap[date].isOutsidePrimaryMonth,
                }"
              >
                <div v-if="viewMode === 'jewish'">
                  <div style="direction: rtl;" class="day-label-1">
                    {{ dayDataMap[date].hebrewDay }}
                  </div>
                  <div class="day-label-2">
                    {{ dayDataMap[date].gregorianLabel }}
                  </div>
                </div>
                <div v-else>
                  <div class="day-label-1">
                    {{ dayDataMap[date].gregorianLabel.split(" ")[1] }}
                  </div>
                  <div style="direction: rtl;" class="day-label-2">
                    {{ dayDataMap[date].hebrewDay }}
                    {{ dayDataMap[date].hebrewMonth }}
                  </div>
                </div>
                <div class="day-label-holiday">
                  {{ dayDataMap[date].holiday }}
                </div>
                <div class="day-label-zmanim">
                  {{ dayDataMap[date].zmanim }}
                </div>
              </div>
            </template>
          </template>
        </v-calendar>
      </v-sheet>
    </v-flex>
    <calendar-export-dialog
      v-model="exportDialog"
      :default-start-date="calendarStartDate"
      :default-end-date="calendarEndDate"
      @export="handleCalendarExport"
    />
    <v-dialog
      v-model="goToMounthDialog"
      max-width="500"
      content-class="dialog-top"
    >
      <v-card>
        <v-card-title
          style="background-color: #5cb860; color: white; justify-content: center;"
          class="headline py-2"
          ><v-icon color="white" left small>mdi-calendar-search</v-icon>Go to
          Month</v-card-title
        >
        <v-card-text>
          <v-layout row wrap>
            <v-flex xs4>
              <v-select
                v-model="goToDate.gregorianMonth"
                :items="gregorianMonths"
                label="Month"
              ></v-select>
            </v-flex>
            <v-flex xs3 class="pl-2">
              <v-text-field
                v-model="goToDate.gregorianYear"
                label="Year"
                :rules="[
                  (value) => {
                    if (isNaN(value) || value < 1240)
                      return 'Enter 4 digit year';
                    if (value > 2239)
                      return 'Enter a valid Gregorian year from 1240 to 2240';
                    return true;
                  },
                ]"
              ></v-text-field>
            </v-flex>
            <v-flex xs4>
              <v-btn
                style="font-weight:600 !important;"
                :disabled="
                  !goToDate.gregorianMonth ||
                    !goToDate.gregorianYear ||
                    isNaN(goToDate.gregorianYear) ||
                    goToDate.gregorianYear < 1240 ||
                    goToDate.gregorianYear > 2239
                "
                flat
                color="success"
                @click="goToGregorianMonth()"
                >{{
                  "Go To - " +
                    goToDate.gregorianMonth +
                    " " +
                    goToDate.gregorianYear
                }}</v-btn
              >
            </v-flex>
          </v-layout>
          <v-layout row wrap>
            <v-flex xs2>
              <v-select
                v-model="goToDate.hebrewMonth"
                :items="hebrewMonths"
                label="חודש"
                style="direction: rtl;"
              >
                <template slot="item" slot-scope="{ item }">
                  <span style="direction: rtl;">{{ item }}</span>
                </template>
              </v-select>
            </v-flex>
            <v-flex xs5 class="pl-2">
              <v-layout row no-wrap>
                <v-flex xs6 class="pr-1">
                  <v-text-field
                    v-model="goToDate.hebrewYear"
                    label="שנת"
                    :rules="[
                      (value) => {
                        if (isNaN(value)) return 'Enter 4 digit year';
                        if (value < 5000) return 'Enter from 5000';
                        if (value > 5999) return 'Enter till 6000';
                        return true;
                      },
                    ]"
                  ></v-text-field>
                </v-flex>
                <v-flex xs6 class="pl-1">
                  <v-select
                    v-model="goToDate.hebrewYear"
                    :items="hebrewYears"
                    item-text="text"
                    item-value="value"
                    style="direction: rtl;"
                  ></v-select>
                </v-flex>
              </v-layout>
            </v-flex>
            <v-flex xs4>
              <v-btn
                style="font-weight:600 !important;"
                :disabled="
                  !goToDate.hebrewMonth ||
                    !goToDate.hebrewYear ||
                    goToDate.hebrewYear < 5000 ||
                    goToDate.hebrewYear > 5999
                "
                flat
                color="success"
                @click="goToHebrewMonth()"
                >{{
                  "Go To - " +
                    goToDate.hebrewMonth +
                    " " +
                    gematriya(goToDate.hebrewYear, 3)
                }}</v-btn
              >
            </v-flex>
          </v-layout>
        </v-card-text>
        <v-card-actions>
          <v-btn
            outline
            @click="goToCurrentMonth()"
            title="Go to Current Month"
            small
          >
            Current Month
          </v-btn>
          <v-spacer></v-spacer>
          <v-btn color="default" small dark @click="goToMounthDialog = false"
            >Close</v-btn
          >
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import Vue from "vue";
import {
  HDate,
  HebrewCalendar,
  getHolidaysOnDate,
  getSedra,
  Locale,
  gematriya,
  gematriyaStrToNum,
  GeoLocation,
  Zmanim,
} from "@hebcal/core";
import moment from "moment";
import CalendarExportDialog from "./CalendarExportDialog.vue";
import { exportCalendarToExcel, exportCalendarToPdf } from "./calendarExport";

const SHORT_MONTHS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];
const ALLOWED_HOLIDAYS = [
  { mask: 128, description: "Rosh Chodesh", majorHoliday: false },
  { mask: 512, description: "Special Shabbat", majorHoliday: false },
  { mask: 256, description: "Minor Fast", majorHoliday: false },
  {
    mask: 1048586,
    description: "Erev Pesach - Sukkot",
    majorHoliday: false,
  },
  { mask: 41, description: "Yom Tov I", majorHoliday: true },
  { mask: 13, description: "Yom Tov II", majorHoliday: true },
  { mask: 2097160, description: "CH''M", majorHoliday: true },
  { mask: 2097162, description: "CH''M", majorHoliday: true },
  { mask: 524288, description: "Minor Holiday", majorHoliday: false },
  { mask: 1048578, description: "Erev Holiday", majorHoliday: false },
  { mask: 1064960, description: "Erev Tish'a B'Av", majorHoliday: false },
  { mask: 16384, description: "Tish'a B'Av", majorHoliday: true },
  { mask: 33, description: "Rosh Hashana I", majorHoliday: true },
  { mask: 5, description: "Rosh Hashana II", majorHoliday: true },
  { mask: 16389, description: "Yom Kippur", majorHoliday: true },
  { mask: 2097154, description: "Hoshana Raba", majorHoliday: true },
  { mask: 1572928, description: "Chanukah: 1 Candle", majorHoliday: false },
  { mask: 524352, description: "Chanukah Candles", majorHoliday: false },
];
const ALLOWED_HOLIDAY_MASK_NUMBER = new Set(
  ALLOWED_HOLIDAYS.map((holiday) => holiday.mask)
);
const ALLOWED_MAJOR_HOLIDAY_MASK_NUMBER = new Set(
  ALLOWED_HOLIDAYS.filter((holiday) => holiday.majorHoliday).map(
    (holiday) => holiday.mask
  )
);
const EXCLUDED_HOLIDAY_DESCRIPTION = new Set([
  "Chag HaBanot",
  "Rosh Hashana LaBehemot",
  "Ta'anit Bechorot",
]);
const ZMANIM_LOCATION = new GeoLocation(
  "New York",
  40.7128,
  -74.006,
  0,
  "America/New_York"
);
const ZMANIM_TIME_FORMAT = new Intl.DateTimeFormat("en-US", {
  hour: "2-digit",
  minute: "2-digit",
});

export default {
  components: {
    CalendarExportDialog,
  },
  data: () => ({
    title: "Calendar",
    viewMode: "jewish",
    hebrewMonth: null,
    gregorianMonth: null,
    loading: false,
    loadingPrint: false,
    dayDataMap: {},
    goToMounthDialog: false,
    exportDialog: false,
    calendarStartDate: "",
    calendarEndDate: "",
    goToDate: {
      gregorianMonth: moment().month() + 1,
      gregorianYear: moment().year(),
      hebrewMonth: "",
      hebrewYear: "",
    },
    gregorianMonths: [
      "January",
      "February",
      "March",
      "April",
      "May",
      "June",
      "July",
      "August",
      "September",
      "October",
      "November",
      "December",
    ],
    hebrewMonths: [
      "תשרי",
      "חשוון",
      "כסלו",
      "טבת",
      "שבט",
      "אדר",
      "אדר א׳",
      "אדר ב׳",
      "ניסן",
      "אייר",
      "סיוון",
      "תמוז",
      "אב",
      "אלול",
    ],
    currentGregorianMonthLabel: "",
    currentHebrewMonthLabel: "",
    language: "he",
  }),
  metaInfo() {
    return {
      title: `${this.title}`,
    };
  },
  created() {
    const today = new HDate(new Date());
    this.hebrewMonth = { month: today.getMonth(), year: today.getFullYear() };
    this.gregorianMonth = {
      month: moment().month() + 1,
      year: moment().year(),
    };

    // this.gregorianPageStart = moment()
    //   .startOf("month")
    //   .format("YYYY-MM-DD");

    this.loadDayData();

    // Suppress Vue warnings
    this._prevWarnHandler = Vue.config.warnHandler;
    Vue.config.warnHandler = (msg, vm, trace) => {
      if (msg.includes(".native modifier")) return;
      (this._prevWarnHandler || console.warn.bind(console, "[Vue warn]:"))(
        msg,
        vm,
        trace
      );
    };
  },

  watch: {
    viewMode(newMode) {
      if (newMode === "gregorian" && this.hebrewMonthDays.length) {
        const date = moment(this.hebrewMonthDays[0].greg()).format(
          "YYYY-MM-DD"
        );
        this.gregorianMonth = {
          month: moment(date).month() + 1,
          year: moment(date).year(),
        };
      } else if (newMode === "jewish" && this.gregorianMonth) {
        const hd = new HDate(
          moment(
            `${this.gregorianMonth.year}-${this.gregorianMonth.month}-01`
          ).toDate()
        );
        this.hebrewMonth = { month: hd.getMonth(), year: hd.getFullYear() };
      }
    },
  },
  computed: {
    hebrewYears() {
      return Array.from({ length: 101 }, (_, index) => {
        const year = 5780 + index;
        return {
          text: gematriya(year, 3),
          value: year,
        };
      });
    },
    hebrewMonthDays() {
      if (!this.hebrewMonth) return [];
      const firstDay = new HDate(
        1,
        this.hebrewMonth.month,
        this.hebrewMonth.year
      );
      return Array.from({ length: firstDay.daysInMonth() }, (_, i) => {
        return new HDate(i + 1, this.hebrewMonth.month, this.hebrewMonth.year);
      });
    },
    calendarStart() {
      if (this.viewMode === "jewish") {
        if (!this.hebrewMonthDays.length) return "";
        return this.toISODate(this.hebrewMonthDays[0].greg());
      } else {
        return `${this.gregorianMonth.year}-${this.gregorianMonth.month}-01`;
      }
    },
    calendarEnd() {
      if (this.viewMode === "jewish") {
        if (!this.hebrewMonthDays.length) return "";
        return this.toISODate(
          this.hebrewMonthDays[this.hebrewMonthDays.length - 1].greg()
        );
      } else {
        return `${this.gregorianMonth.year}-${
          this.gregorianMonth.month
        }-${moment(
          `${this.gregorianMonth.year}-${this.gregorianMonth.month}`,
          "YYYY-M"
        ).daysInMonth()}`;
      }
    },
  },
  methods: {
    gematriya(value, style) {
      if (!value || !style) return;
      return gematriya(value, style);
    },
    setCurrentMonthLabel() {
      const hebrewStart = new HDate(
        new Date(`${this.calendarStartDate}T00:00:00`)
      );
      const hebrewStartMonth = this.formatHebrewMonthName(hebrewStart.greg());
      const hebrewStartYear = gematriya(hebrewStart.getFullYear(), 3);
      const hebrewEnd = new HDate(new Date(`${this.calendarEndDate}T00:00:00`));
      const hebrewEndMonth = this.formatHebrewMonthName(hebrewEnd.greg());
      const hebrewEndYear = gematriya(hebrewEnd.getFullYear(), 3);
      const gregorianMonthStart = new Intl.DateTimeFormat("en-US", {
        month: "long",
      }).format(new Date(`${this.calendarStartDate}T00:00:00`));
      const gregorianMonthEnd = new Intl.DateTimeFormat("en-US", {
        month: "long",
      }).format(new Date(`${this.calendarEndDate}T00:00:00`));

      const hebrewStartMounthAndYear = hebrewStartMonth + " " + hebrewStartYear;
      const hebrewEndMounthAndYear = hebrewEndMonth + " " + hebrewEndYear;

      this.currentHebrewMonthLabel =
        hebrewStartMounthAndYear == hebrewEndMounthAndYear
          ? hebrewStartMounthAndYear
          : hebrewStartYear == hebrewEndYear
          ? hebrewStartMonth + " - " + hebrewEndMounthAndYear
          : hebrewStartMounthAndYear + " - " + hebrewEndMounthAndYear;

      const gregorianStartYear = new Date(
        `${this.calendarStartDate}T00:00:00`
      ).getFullYear();
      const gregorianEndYear = new Date(
        `${this.calendarEndDate}T00:00:00`
      ).getFullYear();
      const gregorianStartMonthAndYear =
        gregorianMonthStart + " " + gregorianStartYear;
      const gregorianEndMonthAndYear =
        gregorianMonthEnd + " " + gregorianEndYear;

      this.currentGregorianMonthLabel =
        gregorianStartMonthAndYear === gregorianEndMonthAndYear
          ? gregorianStartMonthAndYear
          : gregorianStartYear === gregorianEndYear
          ? gregorianMonthStart + " - " + gregorianEndMonthAndYear
          : gregorianStartMonthAndYear + " - " + gregorianEndMonthAndYear;
      this.goToDate.gregorianMonth = gregorianMonthStart;
      this.goToDate.gregorianYear = gregorianStartYear;
      this.goToDate.hebrewMonth = hebrewStartMonth;
      this.goToDate.hebrewYear = gematriyaStrToNum(hebrewStartYear) + 5000;
    },

    async loadDayData() {
      this.dayDataMap = {};
      this.loading = true;
      await new Promise((resolve) => setTimeout(resolve, 0));
      this.dayDataMap = this.computeDayData();
      this.loading = false;
    },
    computeDayData() {
      const map = {};
      const monthDays =
        this.viewMode === "jewish"
          ? this.hebrewMonthDays.map((hd) => ({ hd, greg: hd.greg() }))
          : (() => {
              const result = [];
              const start = moment(
                `${this.gregorianMonth.year}-${this.gregorianMonth.month}-01`
              ).startOf("month");
              const end = moment(
                `${this.gregorianMonth.year}-${this.gregorianMonth.month}-01`
              ).endOf("month");
              for (
                let d = start.clone();
                d.isSameOrBefore(end, "day");
                d.add(1, "day")
              ) {
                const greg = d.toDate();
                result.push({ hd: new HDate(greg), greg });
              }
              return result;
            })();
      this.calendarStartDate = this.toISODate(monthDays[0].greg);
      this.calendarEndDate = this.toISODate(
        monthDays[monthDays.length - 1].greg
      );
      this.setCurrentMonthLabel();
      const monthDateSet = new Set(
        monthDays.map(({ greg }) => this.toISODate(greg))
      );
      const displayStart = moment(monthDays[0].greg).startOf("week");
      const displayEnd = moment(monthDays[monthDays.length - 1].greg).endOf(
        "week"
      );
      const days = [];
      for (
        let d = displayStart.clone();
        d.isSameOrBefore(displayEnd, "day");
        d.add(1, "day")
      ) {
        const greg = d.toDate();
        days.push({ hd: new HDate(greg), greg });
      }
      for (const { greg } of days) {
        const isoDate = this.toISODate(greg);
        map[isoDate] = {
          isOutsidePrimaryMonth: !monthDateSet.has(isoDate),
          ...this.buildDayDataForDate(greg),
        };
      }
      return map;
    },
    toISODate(date) {
      const y = date.getFullYear();
      const m = String(date.getMonth() + 1).padStart(2, "0");
      const d = String(date.getDate()).padStart(2, "0");
      return `${y}-${m}-${d}`;
    },
    prevMonth() {
      let { month, year } = this.hebrewMonth;
      month--;
      if (month < 1) {
        month = HDate.monthsInYear(year);
      } else if (month === 6) {
        year--;
      }
      this.hebrewMonth = { month, year };
      const prevGreg = moment({
        year: this.gregorianMonth.year,
        month: this.gregorianMonth.month - 1,
      }).subtract(1, "month");
      this.gregorianMonth = {
        month: prevGreg.month() + 1,
        year: prevGreg.year(),
      };
    },

    nextMonth() {
      let { month, year } = this.hebrewMonth;
      const maxMonth = HDate.monthsInYear(year);
      month++;
      if (month > maxMonth) {
        month = 1;
      } else if (month === 7) {
        year++;
      }
      this.hebrewMonth = { month, year };
      const nextGreg = moment({
        year: this.gregorianMonth.year,
        month: this.gregorianMonth.month - 1,
      }).add(1, "month");
      this.gregorianMonth = {
        month: nextGreg.month() + 1,
        year: nextGreg.year(),
      };
    },

    goToHebrewMonth() {
      this.viewMode = "jewish";
      const hd = new HDate(
        1,
        this.goToDate.hebrewMonth,
        this.goToDate.hebrewYear
        //gematriyaStrToNum(this.goToDate.hebrewYear) + 5000
      );
      this.hebrewMonth = { month: hd.getMonth(), year: hd.getFullYear() };
      this.goToMounthDialog = false;
    },

    goToGregorianMonth() {
      this.viewMode = "gregorian";
      const parsed = moment(
        `${this.goToDate.gregorianMonth} ${this.goToDate.gregorianYear}`,
        "MMMM YYYY"
      );
      if (!parsed.isValid()) return;
      this.gregorianMonth = { month: parsed.month() + 1, year: parsed.year() };
      this.goToMounthDialog = false;
    },
    goToCurrentMonth() {
      const today = new HDate(new Date());
      this.hebrewMonth = { month: today.getMonth(), year: today.getFullYear() };
      this.gregorianMonth = {
        month: moment().month() + 1,
        year: moment().year(),
      };
      this.goToMounthDialog = false;
    },
    formatHebrewMonthName(gregorianDate) {
      return new Intl.DateTimeFormat("he-u-ca-hebrew", {
        month: "long",
      }).format(gregorianDate);
    },
    formatTimeForRtl(timeText) {
      return `\u200E${timeText}\u200E`;
    },

    async handleCalendarExport({ startDate, endDate }) {
      const rows = this.getExportRows(startDate, endDate);
      exportCalendarToExcel(rows, { startDate, endDate });
      this.exportDialog = false;
    },

    async handleCalendarPrint() {
      this.loadingPrint = true;
      const rows = this.getExportRows(
        this.calendarStartDate,
        this.calendarEndDate
      );
      await exportCalendarToPdf(rows, {
        type: this.viewMode,
        startDate: this.calendarStartDate,
        endDate: this.calendarEndDate,
        titleText:
          this.viewMode === "jewish"
            ? `<h2 style="margin: 0 0 -20px 0;">${
                this.currentHebrewMonthLabel
              }</h2> <br> ${this.currentGregorianMonthLabel}`
            : `<h2 style="margin: 0 0 -20px 0;">${
                this.currentGregorianMonthLabel
              }</h2> <br> ${this.currentHebrewMonthLabel}`,
      });

      this.loadingPrint = false;
    },

    buildDayDataForDate(greg) {
      const hd = new HDate(greg);
      console.log(getHolidaysOnDate(hd, false));
      const holidays = (getHolidaysOnDate(hd, false) || [])
        .filter((h) => ALLOWED_HOLIDAY_MASK_NUMBER.has(h.mask))
        .filter((h) => !EXCLUDED_HOLIDAY_DESCRIPTION.has(h.desc));
      const isMajorHoliday = holidays.some((holiday) =>
        ALLOWED_MAJOR_HOLIDAY_MASK_NUMBER.has(holiday.mask)
      );
      const isKufAlefKislev =
        hd.getMonth() === HDate.monthNum("Kislev") && hd.getDate() === 21;
      const isFriday = greg.getDay() === 5;
      const isSaturday = greg.getDay() === 6;
      const isToday =
        new Date().toLocaleDateString() === greg.toLocaleDateString();
      const sedraInfo = isSaturday
        ? getSedra(hd.getFullYear(), false).lookup(hd)
        : null;
      const parshaText =
        sedraInfo &&
        Array.isArray(sedraInfo.parsha) &&
        sedraInfo.parsha.length &&
        sedraInfo.chag === false
          ? `${sedraInfo.parsha
              .map((name) =>
                Locale.gettext(
                  name,
                  this.language === "he" ? "he" : "ashkenazi"
                )
              )
              .join(" - ")}`
          : "";
      let holidayText = isKufAlefKislev
        ? `${
            this.language === "he"
              ? 'כָּ"א כִּסְלֵו – יוֹם הַצָּלַת רַבֵּנוּ הַקָּדוֹשׁ'
              : "kuf alef kislev - Yom Ha-tzulas Rabbeinu HaKadosh"
          }`
        : holidays.length > 0
        ? holidays
            .map((h) => h.render(this.language === "he" ? "he" : "ashkenazi"))
            .join(", ")
        : "";

      const zmanim = new Zmanim(ZMANIM_LOCATION, greg, false);

      const firstDayOfJewishYear = holidayText.startsWith("רֹאשׁ הַשָּׁנָה") && /\d$/.test(holidayText);
      if (firstDayOfJewishYear) {
        holidayText = "רֹאשׁ הַשָּׁנָה א'"
      }

      return {
        gregorianLabel: SHORT_MONTHS[greg.getMonth()] + " " + greg.getDate(),
        hebrewDay: gematriya(hd.getDate()),
        hebrewMonth: this.formatHebrewMonthName(greg),
        hebrewYear: gematriya(hd.getFullYear(), 3),
        holiday:
          holidayText && parshaText
            ? `${holidayText} | ${parshaText}`
            : holidayText || parshaText,
        zmanim: isFriday
          ? `${
              this.language === "he" ? "הדלקת הנרות" : "Candle Lighting"
            }: ${this.formatTimeForRtl(
              Zmanim.formatTime(zmanim.sunsetOffset(-15), ZMANIM_TIME_FORMAT)
            )}`
          : isSaturday
          ? `${
              this.language === "he" ? "מוצאי שבת" : "Motza'ei Shabbas"
            }: ${this.formatTimeForRtl(
              Zmanim.formatTime(zmanim.sunsetOffset(+72), ZMANIM_TIME_FORMAT)
            )}`
          : "",
        bgColor: isToday
          ? "#e8f7fa"
          : isSaturday || isMajorHoliday
          ? "#fff8e1"
          : "#ffffff",
      };
    },

    getExportRows(startDate, endDate) {
      const start = moment(startDate, "YYYY-MM-DD").startOf("day");
      const end = moment(endDate, "YYYY-MM-DD").startOf("day");
      const rows = [];

      for (
        let day = start.clone();
        day.isSameOrBefore(end, "day");
        day.add(1, "day")
      ) {
        const isoDate = day.format("YYYY-MM-DD");
        const dayData =
          this.dayDataMap[isoDate] || this.buildDayDataForDate(day.toDate());
        rows.push({
          isoDate,
          gregorianDate: isoDate,
          gregorianDay: Number(dayData.gregorianLabel.split(" ")[1]),
          hebrewDate: `${dayData.hebrewDay} ${dayData.hebrewMonth}`,
          hebrewYear: dayData.hebrewYear || "",
          holiday: dayData.holiday || "",
          bgColor: dayData.bgColor || "",
          zmanim: dayData.zmanim || "",
        });
      }
      return rows;
    },
    languageChanged(value) {
      this.language = value;
      this.loadDayData();
    },
  },
};
</script>
<style>
.v-calendar-weekly__head-weekday,
.v-calendar-weekly__head th {
  background-color: #e9eef5 !important;
  color: #5cb860 !important;
  font-weight: 600 !important;
  font-size: 0.85em !important;
  text-transform: uppercase !important;
  letter-spacing: 0.04em !important;
  padding: 0.5em 0.75em !important;
  text-align: center !important;
}

.v-calendar-weekly__day {
  min-height: calc((100vh - 120px) / 7) !important;
  position: relative;
}
.day-bg {
  position: absolute;
  inset: 0;
}
.day-bg-outside-month {
  opacity: 0.35;
}

.day-content-outside-month {
  opacity: 0.55;
}

.day-label-1 {
  font-size: 1.75em;
  position: absolute;
  top: 4px;
  right: 6px;
  color: #5cb860;
  font-weight: 600;
}
.day-label-2 {
  margin-top: 5px;
  position: absolute;
  right: 6px;
  font-size: 0.85em;
  color: #888;
}
.calendar-loading-overlay {
  position: absolute;
  inset: 0;
  z-index: 10;
  background: rgba(255, 255, 255, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
}
.day-label-holiday {
  margin-top: 25px;
  direction: rtl;
  position: absolute;
  right: 6px;
  font-size: 0.85em;
  color: #f44336;
  font-weight: 600;
}
.day-label-zmanim {
  bottom: 4px;
  direction: rtl;
  position: absolute;
  right: 6px;
  font-size: 0.85em;
  color: #1074bb;
  font-weight: 500;
}
.v-btn.cal-action-btn {
  background-color: #fff !important;
  border: 1.5px solid #5cb860 !important;
  border-radius: 6px !important;
  font-weight: 600 !important;
  letter-spacing: 0.04em !important;
  text-transform: uppercase !important;
  font-size: 0.78rem !important;
  box-shadow: 0 1px 4px rgba(92, 184, 96, 0.15) !important;
  transition: box-shadow 0.18s, background-color 0.18s !important;
}
.v-btn.cal-action-btn:hover {
  background-color: #f0faf0 !important;
  box-shadow: 0 3px 10px rgba(92, 184, 96, 0.25) !important;
}
.calendar-container {
  border: 1.5px solid #5cb860 !important;
  border-radius: 3px !important;
}

.calendar-settings-bar {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 10px;
  padding: 3px 6px;
  border-radius: 8px;
}
.calendar-left-actions {
  display: flex;
  flex-direction: column;
}
.calendar-left-actions .v-btn.cal-action-btn {
  margin: 1px !important;
  height: 35px !important;
  min-height: 35px !important;
}
.calendar-settings-divider {
  height: 32px !important;
  border-color: #d4ead5 !important;
}
.calendar-settings-bar .v-btn-toggle {
  border: 1.5px solid #5cb860 !important;
  border-radius: 6px !important;
  overflow: hidden;
  box-shadow: none !important;
}
.calendar-settings-bar .v-btn-toggle .v-btn {
  height: 28px !important;
  padding: 0 12px !important;
  border-radius: 0 !important;
  box-shadow: none !important;
  background-color: #fff !important;
  color: #5cb860 !important;
  font-weight: 600 !important;
  font-size: 0.78rem !important;
  text-transform: none !important;
}
.calendar-settings-bar .v-btn-toggle .v-btn--active {
  background-color: #5cb860 !important;
  color: #fff !important;
}
.calendar-settings-bar .v-btn-toggle .v-btn:hover:not(.v-btn--active) {
  background-color: #f0faf0 !important;
}
</style>
